import { useInfiniteQuery } from "@tanstack/react-query";
import React from "react";
import { useInView } from "react-intersection-observer";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce, useDelayedLoading } from "utilities/usehooks";

interface Config {
  suggestedApiInstruction: server.dto.ListContactAddressBookToItemInstruction
}

export const useAddressbookSearch = (config: Config) => {
  const [searchInput, setSearchInput] = React.useState<string>("");

  const [whichApi, setWhichApi] = React.useState<"suggested" | "search">("suggested");

  const debouncedSearchInput = useDebounce(searchInput, 200);

  const suggestedApi = useCustomQuery({
    queryFn: async () => window.CoreApi.ContactAddressBook.RelatedToItem(config.suggestedApiInstruction),
    queryKey: ["ContactAddressBookRelatedToItem", config.suggestedApiInstruction]
  });

  const searchApi = useInfiniteQuery({
    queryFn: async ({ pageParam }) => window.CoreApi.ContactAddressBook.SearchAddressBook({
      keyword: debouncedSearchInput,
      page: pageParam ?? 1,
      perPage: 50
    }),
    queryKey: ["ContactAddressBookSearch", debouncedSearchInput],
    enabled: whichApi === "search",
    keepPreviousData: true,
    getNextPageParam: (lastPage) => lastPage.data.pagination.page === lastPage.data.pagination.pageCount ? undefined : lastPage.data.pagination.page + 1
  });

  React.useEffect(() => {
    if (debouncedSearchInput.trim() === "") {
      setWhichApi("suggested");
    } else {
      setWhichApi("search");
    }
  }, [debouncedSearchInput]);

  const { inView, ref } = useInView();

  React.useEffect(() => {
    if (inView) {
      searchApi.fetchNextPage();
    }
  }, [inView]);

  const suggestedApiResults = suggestedApi.data?.data ?? [];
  const searchApiResults = searchApi.data?.pages.flatMap(p => p.data.data) ?? [];

  const results = whichApi === "search" ? searchApiResults : suggestedApiResults;

  const shouldIncludeVisibilityRef = (currentIndex: number) => {
    if (whichApi === "suggested") return false;

    return currentIndex === results.length - 3;
  };


  // We only show loading spinners if the cache key is loading for the first time
  // So if you've never searched for "Bob" before, we should show a loading spinner after some time
  // But if you've already searched for "Bob" and the results are cached, we shouldn't show a loading spinner at all
  // (isPreviousData && isFetching) means it doesn't have any cached data to show (it's still showing the data from the previous cache key), so it's the first time loading this cache key
  const isLoadingSearchApiForFirstTime = whichApi === "search" && (searchApi.isPreviousData || searchApi.data == null) && searchApi.isFetching;

  // We only show the loading spinner if the API call takes more than 600ms
  const delayedLoading = useDelayedLoading(600, isLoadingSearchApiForFirstTime);

  return {
    results,
    ref,
    shouldIncludeVisibilityRef,
    searchInput,
    setSearchInput,
    isLoading: suggestedApi.isLoading || delayedLoading,
  };
};